import React from 'react';
import { onSnapshot, doc, collection, getDocs } from 'firebase/firestore';
import { setDoc } from './api/FirestoreInterceptor';
import { db } from './config-firebase/firebase';

export const GlobalContext = React.createContext();

export const GlobalStorage = ({ children }) => {
  const [image, setImage] = React.useState('');
  const [id, setId] = React.useState('');
  const [isToten, setIsToten] = React.useState(() => {
    return JSON.parse(localStorage.getItem('isToten') || 'false');
  });
  const [authorizated, setAuthorizated] = React.useState(false);
  const [userNewRequest, setUserNewRequest] = React.useState({});
  const [pdvRequest, setPdvRequest] = React.useState(false);
  const [orderBeingEdited, setOrderBeingEdited] = React.useState(null);
  const [warningLowRawMaterial, setWarningLowRawMaterial] = React.useState([]);
  const [enableAutoNfce, setEnableAutoNfce] = React.useState(false);
  const [styles, setStyles] = React.useState({
    btnColor: '#b02121',
    secundaryBgColor: '#b02121',
    bgColor: '#b02121',
    fontColor: 'rgb(230, 235, 230)',
    titleFontColor: '#bd2828',
    titleFont: 'Arial',
    textFont: 'sans serif',
  });

  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [packageTier, setPackageTier] = React.useState(1);
  const [hasClients, setHasClients] = React.useState(false);
  const [hasRawMaterial, setHasRawMaterial] = React.useState(false);
  const [hasFinancial, setHasFinancial] = React.useState(false);
  const [canConfigToten, setCanConfigToten] = React.useState(false);
  const [maxDeliveryDistance, setMaxDeliveryDistance] = React.useState(1);
  const [establishmentCep, setEstablishmentCep] = React.useState('');
  const [establishmentCoords, setEstablishmentCoords] = React.useState(null);
  const [pdvMobileView, setPdvMobileView] = React.useState('menu'); // 'menu' ou 'orders'
  const [currentUser, setCurrentUser] = React.useState(null);
  const [currentUserEmail, setCurrentUserEmail] = React.useState(() => {
    return localStorage.getItem('currentUserEmail') || '';
  });

  const loginUser = (email, token) => {
    localStorage.setItem('token', JSON.stringify(token));
    localStorage.setItem('currentUserEmail', email);
    setCurrentUserEmail(email);
  };

  const logoutUser = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUserEmail');
    setCurrentUserEmail('');
    setCurrentUser(null);
  };

  // Listen for global configuration changes
  React.useEffect(() => {
    if (!db) {
      console.warn('Firestore db instance not available in GlobalContext');
      return;
    }
    const unsubscribeNfc = onSnapshot(doc(db, 'GlobalConfig', 'nfcSettings'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.enableAutoNfce !== undefined) {
          setEnableAutoNfce(data.enableAutoNfce);
          localStorage.setItem('enableAutoNfce', JSON.stringify(data.enableAutoNfce));
        }
      }
    });

    const unsubscribePackage = onSnapshot(doc(db, 'GlobalConfig', 'packageSettings'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.packageTier !== undefined) {
          setPackageTier(Number(data.packageTier));
        }
        setHasClients(!!data.hasClients);
        setHasRawMaterial(!!data.hasRawMaterial);
        setHasFinancial(!!data.hasFinancial);
        setCanConfigToten(!!data.canConfigToten);
      }
    });

    const unsubscribeDelivery = onSnapshot(doc(db, 'GlobalConfig', 'deliverySettings'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.maxDeliveryDistance !== undefined) {
          setMaxDeliveryDistance(Number(data.maxDeliveryDistance));
        }
        if (data.establishmentCep !== undefined) {
          setEstablishmentCep(data.establishmentCep);
        }
        if (data.establishmentCoords !== undefined) {
          setEstablishmentCoords(data.establishmentCoords);
        }
      }
    });

    return () => {
      unsubscribeNfc();
      unsubscribePackage();
      unsubscribeDelivery();
    };
  }, []);

  // Pre-cachear imagens localmente para funcionamento rápido e offline
  React.useEffect(() => {
    const toten = JSON.parse(localStorage.getItem('isToten') || 'false');
    if (toten && db) {
      import('./util/imageCache').then((m) => {
        m.precacheAllImages(db);
      });
    }
  }, [db]);

  // [NOVO] Ref global para evitar disparos duplicados de NFC-e em toda a aplicação
  const processedOrdersGlobal = React.useRef(new Set());



  // Listen to current logged-in user changes in Firestore
  React.useEffect(() => {
    if (!db || !currentUserEmail) {
      setCurrentUser(null);
      return;
    }
    const emailClean = currentUserEmail.trim().toLowerCase();
    const unsub = onSnapshot(doc(db, 'admins', emailClean), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setCurrentUser(data);
        localStorage.setItem('currentUserName', data.name || '');
        localStorage.setItem('currentUserRole', data.role || '');
        localStorage.setItem('currentUserPermissions', JSON.stringify(data.permissions || []));
      } else {
        // Autonomia total: se a coleção admins estiver vazia, o primeiro a logar vira Super Admin!
        getDocs(collection(db, 'admins')).then(snapshot => {
          const isFirstUser = snapshot.empty;
          const tempUser = {
            email: emailClean,
            name: emailClean.split('@')[0],
            role: isFirstUser ? 'admin_TOTAL' : 'custom',
            permissions: isFirstUser ? [] : ['requestlist']
          };
          
          // Salva automaticamente o novo usuário no banco para que ele apareça na tabela de Gerência de Acessos
          setDoc(doc(db, 'admins', emailClean), tempUser).catch(e => console.error('Erro ao auto-registrar admin', e));
          
          setCurrentUser(tempUser);
          localStorage.setItem('currentUserName', tempUser.name);
          localStorage.setItem('currentUserRole', tempUser.role);
          localStorage.setItem('currentUserPermissions', JSON.stringify(tempUser.permissions || []));
        });
      }
    }, (err) => {
      console.error('[RBAC] Erro ao monitorar permissões do usuário logado:', err);
    });
    return () => unsub();
  }, [db, currentUserEmail]);

  return (
    <GlobalContext.Provider
      value={{
        image,
        setImage,
        warningLowRawMaterial,
        setWarningLowRawMaterial,
        id,
        setId,
        styles,
        setStyles,
        authorizated,
        setAuthorizated,
        setIsToten,
        isToten,
        userNewRequest,
        setUserNewRequest,
        setPdvRequest,
        pdvRequest,
        orderBeingEdited,
        setOrderBeingEdited,
        processedOrdersGlobal,
        enableAutoNfce,
        setEnableAutoNfce,
        isInitialLoad,
        setIsInitialLoad,
        packageTier,
        hasClients,
        hasRawMaterial,
        hasFinancial,
        canConfigToten,
        setPackageTier,
        pdvMobileView,
        setPdvMobileView,
        maxDeliveryDistance,
        establishmentCep,
        establishmentCoords,
        currentUser,
        setCurrentUser,
        currentUserEmail,
        loginUser,
        logoutUser,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
